import { JSX, RefObject, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

export interface AdminMiniEmailFrameProps {
    /** The message as it was sent or received. */
    html?: string | null;
    /** The plain alternative, shown when there is no HTML. */
    text?: string | null;
}

/** Where an email starts before it has told us how tall it is. */
const OPENING_HEIGHT: number = 180;

/** Beyond this the frame scrolls rather than growing. A newsletter should not own the modal. */
const TALLEST: number = 900;

/** A 1x1 transparent pixel, so a blocked image collapses instead of drawing a broken icon. */
const BLANK_PIXEL: string =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/** Anything that would leave the browser. A `data:` or `cid:` source carries no request. */
const REMOTE: RegExp = /^\s*(https?:)?\/\//i;

/**
 * The email with everything that would hit the network taken out of it.
 *
 * **This is not tidiness, it is the measurement.** Every send here goes out with Mailgun's
 * `o:tracking-opens`, so the HTML carries a 1x1 pixel on the sending domain and *requesting it is
 * what records an open*. Rendering a conversation in HQ would log an open against the recipient
 * for every message an admin read - and on a mystery shop, whether and when the target engaged is
 * the entire thing the run exists to measure. An admin scrolling a transcript would quietly
 * corrupt their own result.
 *
 * **Rewritten rather than blocked by policy.** The first attempt injected a
 * `<meta http-equiv="Content-Security-Policy">` into the document, which is the tidier-looking
 * answer and did not work: the pixel still fired, measured in a browser on 2026-09-11. Editing the
 * markup cannot be ignored by anything, and it is testable without a network.
 *
 * `data:` and `cid:` sources are left alone: an inlined logo is already in the document and tells
 * nobody anything. The original markup is kept by the caller, so "Load images" simply renders it
 * unmodified rather than trying to undo this.
 */
function withRemoteContentBlocked(html: string): string {
    return html
        // Every `src` and `background` pointing off-document, on any element. A tracking pixel is
        // an ordinary `<img>`, which is exactly why this cannot be narrowed to suspicious ones.
        .replace(
            /\s(src|background)\s*=\s*("([^"]*)"|'([^']*)')/gi,
            (whole: string, attribute: string, _quoted: string, double?: string, single?: string): string => {
                const value: string = double ?? single ?? '';

                return REMOTE.test(value)
                    ? ` ${ attribute }="${ BLANK_PIXEL }" data-blocked-src="1"`
                    : whole;
            }
        )
        // Background images in a `style` attribute or a `<style>` block reach the network just as
        // an `<img>` does, and a pixel can perfectly well be set that way.
        .replace(/url\(\s*['"]?\s*(https?:)?\/\/[^)]*\)/gi, 'none')
        // `srcset` carries its own list of URLs and is not covered by the `src` rule above.
        .replace(/\ssrcset\s*=\s*("[^"]*"|'[^']*')/gi, '');
}

/**
 * One email, rendered in its own document so its styles are its own.
 *
 * **An email carries its own CSS and it is written for a mail client, not for HQ.** The Nok
 * templates are full documents with `<style>` blocks, table layouts and inline widths; dropping
 * that markup into the page would either lose all of it - `strip_tags`, which is what the plain
 * body already is - or let it reach out and restyle the modal around it. A frame is the only place
 * both problems go away at once.
 *
 * **Sandboxed with `allow-same-origin` and nothing else.** No `allow-scripts`, so nothing in a
 * received email runs: these are messages from strangers, which is the entire premise of a mystery
 * shop. `allow-same-origin` is there so the frame's height can be measured after it loads, and it
 * is safe **only** because scripts are off - the two together would let a document remove its own
 * sandbox.
 *
 * The height is measured rather than fixed, because a two-line reply and a full newsletter both
 * arrive here and a fixed frame is wrong for both. It is capped so one long email cannot push the
 * conversation below it out of reach.
 */
export const AdminMiniEmailFrame: (props: AdminMiniEmailFrameProps) => JSX.Element =
    (props: AdminMiniEmailFrameProps): JSX.Element => {
        const frameRef: RefObject<HTMLIFrameElement | null> = useRef<HTMLIFrameElement>(null);
        const [height, setHeight] = useState<number>(OPENING_HEIGHT);
        // Per message, and never remembered. A preference that outlived the modal would mean an
        // admin firing tracking pixels for the rest of the session without being asked again.
        const [showImages, setShowImages] = useState<boolean>(false);

        const html: string = (props.html ?? '').trim();
        const text: string = (props.text ?? '').trim();

        // No HTML half. Plain text keeps its own line breaks, which `white-space` preserves and a
        // frame would not add anything to.
        if (html === '') {
            return (
                <Typography sx={ {
                    fontSize: '13px',
                    color: 'text.secondary',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                } }>
                    { text === '' ? '(this message has no body)' : text }
                </Typography>
            );
        }

        function measure(): void {
            const document: Document | null | undefined = frameRef.current?.contentDocument;
            if (!document?.body) {
                return;
            }

            // `scrollHeight` on the document element rather than the body: an email whose body is
            // absolutely positioned or floated reports a body height of nearly nothing.
            const measured: number = Math.max(
                document.body.scrollHeight,
                document.documentElement?.scrollHeight ?? 0
            );

            setHeight(Math.min(Math.max(measured + 8, 60), TALLEST));
        }

        return (
            <Box sx={ {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '6px',
                overflow: 'hidden',
                background: '#ffffff',
            } }>
                { /* The warning is visible rather than in a tooltip, for two reasons. The
                     consequence of clicking is not obvious, and hiding it behind a hover means it
                     is read only by somebody who already suspected. And MUI's `Tooltip` puts its
                     title on the child as `aria-label`, which **replaces** the button's accessible
                     name - a screen reader announced the whole sentence as the name of the button
                     instead of "Load images". */ }
                { !showImages && (
                    <Box sx={ {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                        padding: '8px 10px',
                        borderBottom: '1px solid',
                        borderBottomColor: 'divider',
                        background: 'action.hover',
                    } }>
                        <Box sx={ { minWidth: 0 } }>
                            <Typography sx={ { fontSize: '11px', fontWeight: 700, color: 'text.secondary' } }>
                                { 'Remote images blocked' }
                            </Typography>
                            <Typography sx={ { fontSize: '11px', color: 'text.secondary' } }>
                                { 'Loading them records an open against the recipient.' }
                            </Typography>
                        </Box>
                        <Button size="small"
                                startIcon={ <ImageOutlinedIcon fontSize="small" /> }
                                onClick={ (): void => setShowImages(true) }
                                sx={ { fontSize: '11px', flexShrink: 0 } }>
                            { 'Load images' }
                        </Button>
                    </Box>
                ) }

                <iframe
                    ref={ frameRef }
                    title="Email content"
                    // No `allow-scripts`. See the class note above: this is somebody else's markup.
                    sandbox="allow-same-origin"
                    // `srcDoc` rather than a blob or a data URL, so nothing is written to disk and
                    // no object URL is left to revoke when the modal closes.
                    srcDoc={ showImages ? html : withRemoteContentBlocked(html) }
                    onLoad={ measure }
                    style={ {
                        display: 'block',
                        width: '100%',
                        height: `${ height }px`,
                        border: 'none',
                        // Emails are written against a white ground whatever HQ's theme is doing,
                        // and a transparent frame over a dark panel renders black on black.
                        background: '#ffffff',
                    } }
                />
            </Box>
        );
    };
